import { createReducer } from './general-helpers';

const START = 'start';
const SUCCESS = 'success';
const FAIL = 'fail';
const RESET = 'reset';

export const initialState = {
  isFetching: false,
  data: null,
  errorInfo: null,
  activeQuery: null,
  successQuery: null,
  failQuery: null,
  hasError: false,
  startTime: 0,
  failTime: 0,
  successTime: 0,
};

function getKey(key, progress) {
  return `${key}.${progress}`;
}

function getAction(key, progress, data, args) {
  return {
    type: getKey(key, progress),
    data,
    args,
  };
}

export function createStartAction(key, data, args) {
  return getAction(key, START, data, args);
}

export function createSuccessAction(key, data, args) {
  return getAction(key, SUCCESS, data, args);
}

export function createFailAction(key, err, args) {
  return getAction(key, FAIL, err, args);
}

export function createResetAction(key) {
  return getAction(key, RESET);
}

export function createBaseAsyncHandlers(key, transformer = data => data) {
  return {
    [getKey(key, START)]: (state, action) => {
      return {
        ...state,
        isFetching: true,
        activeQuery: action.data,
        startTime: new Date().valueOf(),
        // Prior data or error is persisted so it can continue to render
      };
    },
    [getKey(key, SUCCESS)]: (state, action) => {
      return {
        ...state,
        isFetching: false,
        data: transformer(action.data),
        errorInfo: null,
        hasError: false,
        successQuery: state.activeQuery,
        activeQuery: null,
        successTime: new Date().valueOf(),
      };
    },
    [getKey(key, FAIL)]: (state, action) => {
      return {
        ...state,
        isFetching: false,
        errorInfo: action.data,
        hasError: true,
        failQuery: state.activeQuery,
        activeQuery: null,
        failTime: new Date().valueOf(),
      };
    },
    [getKey(key, RESET)]: () => {
      return initialState;
    },
  };
}

export function createBaseAsyncReducer(key, transformer) {
  return createReducer(initialState, createBaseAsyncHandlers(key, transformer));
}

/**
 * If true, is in a pristine "never tried to load" state
 */
export function hasNoQuery(state) {
  return !(state.activeQuery || state.successQuery || state.failQuery);
}

/**
 * This is typically used for one-time fetches, where you want to present
 * a loading indicator if the fetch is in progress or has not yet begun.
 */
export function isPending(state) {
  return !(state.successQuery || state.failQuery);
}

export function makeDataUpdater(func) {
  return (state, action) => {
    return {
      ...state,
      data: func(state.data, action),
    };
  };
}

export function makeDataUpdaterRequireData(func) {
  return (state, action) => {
    if (state === null || state === undefined) {
      return state;
    }
    if (state.data === null || state.data === undefined) {
      return state;
    }
    return {
      ...state,
      data: func(state.data, action),
    };
  };
}

/**
 * This uses the existing async handlers, but rather than being a dumb data dump,
 * it creates an object that is keyed by some property of the response. This is
 * typically most useful for loading a bunch of data from the same endpoint by id.
 *
 * e.g.
 * Normal start action excerpt: { type: "fake.start", data: { id: "abc" }}
 * Normal success action excerpt: { type: "fake.success", data: { name: "Test 123" }, args: [{ id: "abc" }]}
 * Output: { "abc": <itemReducer output on above actions>}
 *
 * @param actionKeys Base keys that must be present for the item reducer to be called
 * @param param The parameter in the args to index on
 * @param itemReducer The item reducer to call to generate the individual entries.
 *                    Defaults to the standard async reducer if unspecified
 * @param globalReducer If the item reducer does not match, call the optional globalReducer on the full state tree
 * @param undefinedUpdateReducer If the key is undefined, call the optional undefinedUpdateReducer on the existing
 *                               data for the undefined key
 */
export function createKeyedAsyncReducer({
  actionKeys,
  param,
  itemReducer,
  globalReducer,
  undefinedUpdateReducer,
}) {
  if (typeof actionKeys === 'string') {
    actionKeys = [actionKeys];
  }
  if (!itemReducer) {
    itemReducer = createBaseAsyncReducer(actionKeys[0]);
  }
  if (!globalReducer) {
    globalReducer = state => state;
  }

  function runReducers(itemKey, state, action) {
    let undefinedUpdate = {};
    if (undefinedUpdateReducer && itemKey !== undefined && state.undefined) {
      undefinedUpdate.undefined = undefinedUpdateReducer(
        state.undefined,
        action
      );
    }

    return {
      ...state,
      ...undefinedUpdate,
      [itemKey]: itemReducer(state[itemKey], action),
    };
  }

  const startKeys = actionKeys.map(k => getKey(k, START));
  const endKeys = actionKeys
    .map(k => getKey(k, FAIL))
    .concat(actionKeys.map(k => getKey(k, SUCCESS)));

  return function(state = {}, action) {
    if (startKeys.indexOf(action.type) !== -1) {
      const itemKey = action.data[param] || action.data[0][param];
      return runReducers(itemKey, state, action);
    } else if (endKeys.indexOf(action.type) !== -1) {
      const itemKey = action.args[0][param];
      return runReducers(itemKey, state, action);
    }
    return globalReducer(state, action);
  };
}



// WEBPACK FOOTER //
// ./src/app/api/redux-util/async-helpers.js